import {
    Dispatch,
    Fragment,
    SetStateAction,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { classNames } from '@progress/kendo-react-common'
import { DropPosition } from '../common'
import React from 'react'
import Translation from 'components/Translation'
import { getTranslation } from 'utils/language-helper'
import { useAppContext } from 'context/AppContext'
import { Tooltip } from '@progress/kendo-react-tooltip';

type GenericSelectorProps = {
    allow_select_all?: boolean
    class_name?: string
    position?: DropPosition
    items: any[]
    selected?: any
    onCompleteSelection?(selections: any[]): void
    setSelectedItems: Dispatch<SetStateAction<any[]>>
    selected_items: any[]
    is_open?: boolean
    setIsOpen?: Dispatch<SetStateAction<boolean>>
    disable?: boolean
    item_name?: string
    icon?: string
    is_checkbox?: boolean
}

GenericSelector.defaultProps = {
    allow_select_all: true,
    is_checkbox: false,
    item_name: "Items"
}

export function GenericSelector(props: GenericSelectorProps) {
    const { translations } = useAppContext()
    const [is_opened, openMenu] = useState(false)
    const [title, setTitle] = useState<string>('')
    const [all_title, setAllTitle] = useState("")
    const title_label_ref = React.createRef<HTMLSpanElement>()
    const tooltip_ref = React.useRef<Tooltip>(null)
    const [is_title_label_overflowed, toggleTitleLabelOverflow] = useState<boolean>(false)

    useEffect(() => {
        if (props?.allow_select_all) {
            setAllTitle(getTranslation("all_x" || "", translations, {
                val: props.item_name as string
            }))
        }
    }, [translations])

    useLayoutEffect(() => {
        toggleTitleLabelOverflow(!!title_label_ref?.current && !!title_label_ref.current?.clientWidth && !!title_label_ref.current?.scrollWidth && title_label_ref.current?.clientWidth < title_label_ref.current.scrollWidth)
    }, [title_label_ref])

    const selectItemHandler = (item: any, is_checked: boolean) => {
        let selected_items, title
        if (props.is_checkbox) {
            if (is_checked) {
                props.selected_items?.push(item)
                selected_items = props.selected_items.slice() || [item]
            } else {
                selected_items = props.selected_items?.filter((selected) => selected.id != item.id) || [] //not sure if filter or splice is better, should look into this again
            }
            title = selected_items.length == props.items.length ? all_title : selected_items.length > 1 ? `${selected_items.length} ${getTranslation(props?.item_name || "Items", translations)}` : item?.translation && item.translation.length > 0 ? getTranslation(item.translation, translations) : item.text || item.name
        } else {
            selected_items = [item]
            title = item?.translation && item.translation.length > 0 ? getTranslation(item.translation, translations) : item.text || item.name
        }
        props.setSelectedItems(selected_items)
        setTitle(title)
    }

    const selectAllHandler = (is_checked: boolean) => {
        let selected_items
        if (props.is_checkbox && !is_checked) {
            selected_items = []
            setTitle(`0 ${getTranslation(props?.item_name || "Items", translations)}`)
        } else {
            selected_items = props.items
        }
        props.setSelectedItems(selected_items)
    }

    function translateSelected() {
        let selected = props.selected_items[0]
        return (
            selected?.translation
                && selected.translation.length > 0
                ? getTranslation(selected.translation, translations)
                : selected?.text || selected?.name)
    }

    return (
        <Menu
            as="div"
            className={classNames(
                'relative inline-block text-left',
                props.class_name
            )}
        >
            {({ open }) => (
                <>
                    <Menu.Button
                        disabled={props.disable || false}
                        className={classNames(
                            'inline-flex justify-between items-center w-full rounded-md border  p-2 bg-white text-sm font-medium text-gray-700',
                            props.disable
                                ? 'bg-gray-100 hover:bg-gray-200 cursor-not-allowed'
                                : 'hover:bg-gray-50 '
                        )}
                        onClickCapture={() => {
                            openMenu(!is_opened)
                            props.setIsOpen && props.setIsOpen(false)
                        }}
                    >
                        <div className='flex flex-row items-center'>
                            <i className={classNames("text-sm mr-2 text-custom-dark-cyan-blue", props?.icon)} />
                            <span ref={title_label_ref} title={is_title_label_overflowed ? title || translateSelected() : ""} className="truncate max-w-[125px] block"
                                onMouseOver={event => tooltip_ref.current && tooltip_ref.current.handleMouseOver(event)}
                                onMouseOut={event => tooltip_ref.current && tooltip_ref.current.handleMouseOut(event)}
                            >
                                {props.items.length == props.selected_items.length && props.allow_select_all
                                    ?
                                    all_title
                                    : title || translateSelected()}
                            </span>
                            {is_title_label_overflowed &&
                                <Tooltip ref={tooltip_ref} anchorElement="target" position="top"></Tooltip>
                            }
                        </div>
                        <ChevronDownIcon
                            className="-mr-1 ml-2 h-5 w-5 text-custom-dark-cyan-blue"
                            aria-hidden="true"
                        />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                        show={open}
                    >
                        <Menu.Items
                            static
                            className={classNames(
                                props.position,
                                'absolute mt-2 w-56',
                                'rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5',
                                'focus:outline-none'
                            )}
                        >
                            <div className="py-1">
                                {props.allow_select_all ? (
                                    <div>
                                        <Menu.Item key={'all_items'}>
                                            {({ active }) => (
                                                <div
                                                    className={classNames(
                                                        active
                                                            ? 'bg-gray-100 text-gray-900'
                                                            : 'text-gray-700',
                                                        'block text-sm',
                                                        'mb-2'
                                                    )}
                                                >
                                                    <div className="mt-1 ml-2">
                                                        <label className="inline-flex items-center">
                                                            <input
                                                                type={props.is_checkbox ? "checkbox" : "radio"}
                                                                className={props.is_checkbox ? "h-4 w-4 mr-2 border-gray-300 rounded text-primary focus:ring-primary-light" : "form-radio h-6 w-6"}
                                                                name={`selector-${props.item_name}`}
                                                                value={'all'}
                                                                defaultChecked={
                                                                    props.items.length == props.selected_items.length
                                                                }
                                                                onChange={
                                                                    (e) => {
                                                                        selectAllHandler(e.target.checked)
                                                                    }
                                                                }
                                                            />
                                                            <span className="ml-4">
                                                                {all_title}
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </Menu.Item>
                                        <hr className="mb-2" />
                                    </div>
                                ) : (
                                    <></>
                                )}

                                {props.items && props.items.length
                                    ? props.items.map(
                                        (
                                            i: Record<string, string>,
                                            idx
                                        ) => {
                                            return (
                                                <Menu.Item key={i.id}>
                                                    {({ active }) => (
                                                        <div
                                                            className={classNames(
                                                                active
                                                                    ? 'bg-gray-100 text-gray-900'
                                                                    : 'text-gray-700',
                                                                'block text-sm',
                                                                'mb-2'
                                                            )}
                                                        >
                                                            <div className="mt-1 ml-2">
                                                                <label className="inline-flex items-center">
                                                                    <input
                                                                        type={props.is_checkbox ? "checkbox" : "radio"}
                                                                        className={props.is_checkbox ? "h-4 w-4 mr-2 border-gray-300 rounded text-primary focus:ring-primary-light" : "form-radio h-6 w-6"}
                                                                        name={`selector-${props.item_name}`}
                                                                        value={
                                                                            i.id
                                                                        }
                                                                        defaultChecked={
                                                                            props.items.length == props.selected_items.length && !props.is_checkbox
                                                                                ? false
                                                                                : props.selected_items?.some((item: any) => item.id == i.id)
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            selectItemHandler(
                                                                                i,
                                                                                e
                                                                                    .target
                                                                                    .checked
                                                                            )
                                                                        }
                                                                    />
                                                                    <span className="ml-4">
                                                                        {i?.translation && i.translation.length > 0 ? (
                                                                            <Translation
                                                                                content_key={i.translation}
                                                                            />
                                                                        ) : (
                                                                            <>{i.text || i.name}</>
                                                                        )}
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Menu.Item>
                                            )
                                        }
                                    )
                                    : ''}
                            </div>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    )
}

export default GenericSelector
